import { useEffect, useMemo } from "react";
import { useLingui } from "@lingui/react";
import { useLocation } from "react-router";
import { StatCard } from "@/components/ui/stat-card";
import { formatMetricValue, metricStatDelta, periodTotalCaption } from "@/lib/metrics/metricFormat";
import { useMetricSeries } from "@/lib/metrics/useMetricData";
import type { MetricScopeParams } from "@/lib/metrics/series";
import type { MetricDefinition } from "@/lib/metrics/types";
import { isOrgWideUnderScope, metricCardHref } from "./metricLinks";
import { MetricSparkline, type SparkPoint } from "./MetricSparkline";
import { StarButton } from "./StarButton";

export interface MetricCardProps {
  def: MetricDefinition;
  from: string;
  to: string;
  scope: MetricScopeParams;
  currency: string;
  /** False while the card's section is closed: no request, no figure. */
  enabled?: boolean;
  isFavourite: boolean;
  onToggleFavourite: () => void;
  /**
   * Reports when the card's figure was loaded (0 when closed or unmounted),
   * under `freshnessKey`. Must be a stable callback.
   */
  onUpdatedAt?: (key: string, updatedAt: number) => void;
  /** The card's key for `onUpdatedAt`; the same metric can show twice (My metrics). */
  freshnessKey?: string;
  /** Overrides where the card leads (outside Analytics, the Dashboard passes its own). */
  href?: string;
  /** Drops the basis line (the Dashboard's My metrics block). */
  compact?: boolean;
  testId?: string;
}

/**
 * One metric of the Kennzahlen hub (#2175 B2): label, basis caption, 12-month
 * sparkline (the pointer reads month and value), the period value and its
 * change against the previous year. The whole card opens the metric's detail,
 * or the silo section that owns the metric; the star pins it to My metrics.
 */
export function MetricCard({
  def,
  from,
  to,
  scope,
  currency,
  enabled = true,
  isFavourite,
  onToggleFavourite,
  onUpdatedAt,
  freshnessKey,
  href,
  compact = false,
  testId,
}: MetricCardProps) {
  const { i18n } = useLingui();
  const location = useLocation();
  const series = useMetricSeries(def, from, to, scope, { enabled });
  const updatedAt = series.query.dataUpdatedAt;

  const reportKey = freshnessKey ?? def.id;
  useEffect(() => {
    onUpdatedAt?.(reportKey, enabled ? updatedAt : 0);
  }, [onUpdatedAt, reportKey, enabled, updatedAt]);
  useEffect(() => () => onUpdatedAt?.(reportKey, 0), [onUpdatedAt, reportKey]);

  // The sparkline's months: the last twelve with a value, oldest first.
  const sparkPoints = useMemo<SparkPoint[]>(
    () =>
      series.points
        .filter((p): p is typeof p & { value: number } => p.value !== null && Number.isFinite(p.value))
        .slice(-12)
        .map((p) => ({ period: p.period, value: p.value })),
    [series.points],
  );

  const label = i18n._(def.label);
  const value = formatMetricValue(def.unit, series.total, i18n, currency);
  const caption = def.forward
    ? def.forwardWindow
      ? i18n._(def.forwardWindow)
      : i18n._({ id: "analytics.metrics.window.year", message: "Calendar year {year}", values: { year: to.slice(0, 4) } })
    : periodTotalCaption(def.aggregate, from, to, series.lastPeriod, i18n);
  const orgWide = isOrgWideUnderScope(def.scope, scope);

  return (
    <div className="relative" data-testid={testId ?? `metric-card-${def.id}`}>
      <StatCard
        className="pr-11"
        label={label}
        title={compact ? i18n._(def.basis) : undefined}
        value={value || "–"}
        delta={metricStatDelta(def, series.delta, i18n)}
        href={href ?? metricCardHref(location.pathname, location.search, def.id)}
        isLoading={enabled && series.query.isLoading}
        isError={series.query.isError}
        onRetry={() => void series.query.refetch()}
        visual={
          sparkPoints.length > 1 ? (
            <MetricSparkline points={sparkPoints} format={(v) => formatMetricValue(def.unit, v, i18n, currency)} />
          ) : undefined
        }
        hint={
          <>
            {compact ? null : <span className="block">{i18n._(def.basis)}</span>}
            <span className={compact ? "block" : "mt-1 block"}>
              {value || series.query.isLoading
                ? caption
                : i18n._({ id: "analytics.metrics.noData", message: "No data in this period" })}
              {orgWide
                ? ` · ${i18n._({ id: "analytics.metrics.scope.all", message: "All locations" })}`
                : null}
            </span>
          </>
        }
      />
      <StarButton
        className="absolute right-2 top-2"
        starred={isFavourite}
        onToggle={onToggleFavourite}
        metricLabel={label}
      />
    </div>
  );
}
